/**
 * Agora Real-Time STT decoder + ingest pipeline.
 *
 * Agora's Real-Time STT bot publishes transcription results back into the
 * RTC channel as Protobuf-encoded "stream-message" payloads (see
 * https://docs.agora.io/en/real-time-stt/develop/parse-data). The bot's
 * UID is whatever we passed as `pubBotUid` when we created the STT agent
 * via /api/meetings/:id/transcription/start. We:
 *
 *   1. Listen for stream-message events from that specific UID.
 *   2. Decode the Protobuf payload using a runtime-built schema (no
 *      generated file needed — the schema is small and stable).
 *   3. POST every finalized utterance to our backend ingest endpoint.
 *
 * Secrets never touch the browser: we only see the STT bot's UID, never
 * the Agora REST customer-id/secret. The backend issues `start` /
 * `leave` via Basic auth, and we just ride along on the RTC data stream
 * we're already subscribed to for the meeting.
 *
 * NOTE: Browser SpeechRecognition is intentionally NOT used as a
 * fallback for the meeting transcript. It only captures the local
 * user's microphone and would produce a misleading "everyone said X"
 * transcript that AI Summary would then read.
 */
import { useEffect, useRef } from "react";
import protobuf from "protobufjs";
import api from "@/lib/api";

// Agora STT v7 protobuf schema. Mirrors the OFFICIAL SttMessage.proto
// published at https://docs.agora.io/en/real-time-stt/develop/parse-data
// EXACTLY — package name, field tag numbers, and types all match.
// Earlier versions of this hook used a guessed schema with the package
// name "AgoraSpeech2TextProtobuffer" and tag numbers that didn't match
// the wire format, which caused every decode to either throw or return
// blank fields (so we'd silently drop every utterance). DO NOT change
// these tag numbers without verifying against Agora's docs again.
const STT_PROTO = `
syntax = "proto3";

package Agora.SpeechToText;

message Text {
  reserved 1 to 3, 5, 7 to 9, 11, 17;
  int64 uid = 4;
  int64 time = 6;
  repeated Word words = 10;
  int32 duration_ms = 12;
  string data_type = 13;
  repeated Translation trans = 14;
  string culture = 15;
  int64 text_ts = 16;
  OriginalTranscript original_transcript = 18;
  int64 sentence_id = 19;
}

message Word {
  reserved 2, 3, 5;
  string text = 1;
  bool is_final = 4;
}

message Translation {
  bool is_final = 1;
  string lang = 2;
  repeated string texts = 3;
}

message OriginalTranscript {
  string culture = 1;
  repeated Word words = 2;
}
`;

let _root = null;
let _TextType = null;
function ensureProto() {
  if (_TextType) return _TextType;
  _root = protobuf.parse(STT_PROTO).root;
  _TextType = _root.lookupType("Agora.SpeechToText.Text");
  return _TextType;
}

/**
 * Listen for Agora STT messages on the RTC client and POST finalized
 * utterances to our backend.
 *
 * @param {Object}  params
 * @param {Object}  params.client        - AgoraRTCClient instance
 * @param {string}  params.meetingId     - bloq meeting id (mtg_…)
 * @param {boolean} params.enabled       - whether the listener should be active
 * @param {number}  params.botUid        - the STT bot's pubBotUid (from /start)
 */
export default function useAgoraSttDecoder({ client, meetingId, enabled, botUid }) {
  // Keep a small in-memory cache of dedupe keys so a rapid duplicate
  // doesn't even leave the browser. The backend has its own dedupe;
  // this is just bandwidth-saving.
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!client || !meetingId || !enabled || !botUid) return;
    let cancelled = false;
    const TextType = ensureProto();

    const handler = async (uid, payload) => {
      // Debug breadcrumb — visible in browser console while we verify
      // the SDK is delivering messages. The STT bot's pubBotUid is what
      // we passed into the v7 join request; non-bot data-stream
      // messages (e.g. chat-over-data-stream) are silently ignored.
      // eslint-disable-next-line no-console
      console.log("[agoraStt] stream-message from uid=", uid, "expected botUid=", botUid, "bytes=", payload?.length);
      // Only consume messages emitted by the STT bot itself. The bot's
      // uid arrives here as a string or number depending on SDK build.
      if (String(uid) !== String(botUid)) return;
      try {
        const message = TextType.decode(new Uint8Array(payload));
        // protobufjs deserializes snake_case proto fields as camelCase
        // in JS objects (e.g. `data_type` -> `dataType`). Make sure
        // every field access below uses the camelCase form — an
        // earlier version of this hook used snake_case and silently
        // dropped every utterance because every read returned undefined.
        const obj = TextType.toObject(message, { longs: Number, enums: Number, defaults: true });
        // eslint-disable-next-line no-console
        console.log("[agoraStt] decoded", obj);

        // dataType is "transcribe" or "translate" per the official schema.
        if ((obj.dataType || "transcribe") !== "transcribe") return;

        const words = Array.isArray(obj.words) ? obj.words : [];
        if (!words.length) return;
        const text = words.map((w) => w.text || "").join("").trim();
        if (!text) return;

        // Agora's official schema: `Text.time` is only set on FINAL
        // sentence results, and every word also carries `isFinal`. A
        // sentence is final when every word.isFinal === true AND
        // Text.time > 0.
        const allWordsFinal = words.every((w) => w.isFinal === true);
        const sentenceTime = Number(obj.time || 0);
        const isFinal = allWordsFinal && sentenceTime > 0;
        if (!isFinal) return; // partials are surfaced by the live transcript poll, not persisted

        const durationMs = Number(obj.durationMs || 0);
        const startMs = Math.max(0, sentenceTime - durationMs);
        const endMs = sentenceTime;
        const speakerUid = Number(obj.uid || 0);

        // Local dedupe — same payload often arrives twice when Agora
        // resends an unreceived data-stream packet. sentenceId is the
        // canonical key when present.
        const sentenceId = String(obj.sentenceId || `${speakerUid}-${sentenceTime}`);
        const key = `${speakerUid}:${sentenceId}:${text.slice(0, 60)}`;
        if (seenRef.current.has(key)) return;
        seenRef.current.add(key);
        // Cap the cache so it doesn't grow unbounded over a long call.
        if (seenRef.current.size > 1000) {
          seenRef.current = new Set(Array.from(seenRef.current).slice(-500));
        }

        const culture = obj.culture || "en-US";

        if (cancelled) return;
        try {
          await api.post(`/meetings/${meetingId}/transcription/ingest`, {
            agora_uid: speakerUid,
            text,
            is_final: true,
            // Agora's schema doesn't carry word-level confidence here;
            // record 0 to keep the column truthful.
            confidence: 0,
            start_time_ms: startMs,
            end_time_ms: endMs,
            language: culture,
          });
        } catch (err) {
          // Treat 409 (no active agora transcript) and 404 as "stop trying
          // until the user re-enables"; everything else just logs.
          // eslint-disable-next-line no-console
          console.warn("[agoraStt] ingest failed", err?.response?.status, err?.response?.data);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[agoraStt] decode failed", e?.message || e);
      }
    };

    try {
      client.on("stream-message", handler);
      // Also log every user-joined event so we can verify the STT bot
      // is actually arriving in the channel. Without this, a missing
      // stream-message could mean either "bot never joined" or "bot
      // joined but isn't publishing data" — very different bugs.
      const onUserJoined = (rUser) => {
        // eslint-disable-next-line no-console
        console.log("[agoraStt] user-joined uid=", rUser?.uid, "(expected botUid=", botUid, ")");
      };
      try { client.on("user-joined", onUserJoined); } catch {}
      // eslint-disable-next-line no-console
      console.log("[agoraStt] decoder attached for meeting", meetingId, "botUid", botUid);
    } catch { /* SDK without stream-message */ }
    return () => {
      cancelled = true;
      try { client.off("stream-message", handler); } catch {}
    };
  }, [client, meetingId, enabled, botUid]);
}
